/**
 * Shared channel stream URL resolution + schedule boundary helpers.
 * Used by ChannelDetailPage and ChannelTunePage — single source of truth for Watch Live.
 */
import { sanitizePlaybackUrl } from "../../video-player/production/buildHostPlayerConfig";

/**
 * Ordered failover candidates from Media Asset + optional content detail playback.
 * @param {object|null} tuned  controlPlaneFromChannelNow result
 * @param {object|null} detail content/show DTO
 * @returns {string[]}
 */
export function collectChannelStreamCandidates(tuned, detail) {
  const asset =
    (tuned && tuned.plane && tuned.plane.mediaAsset) ||
    (detail && detail.playback && detail.playback.media_asset) ||
    null;
  const candidates = [];
  if (asset) {
    const primary = asset.primary || {};
    const meta = asset.metadata || {};
    [primary.url, primary.src, meta.stream_source_url, meta.backup_stream_url].forEach(
      function (u) {
        if (u) candidates.push(String(u));
      }
    );
  }
  if (detail && detail.playback && detail.playback.url) {
    candidates.push(String(detail.playback.url));
  }
  return candidates;
}

/**
 * Resolve the first allowlisted playable URL.
 * @param {object|null} tuned
 * @param {object|null} detail
 * @param {number} [failoverIndex]
 * @returns {string}
 */
export function resolveChannelStreamUrl(tuned, detail, failoverIndex) {
  const idx = Math.max(0, failoverIndex || 0);
  const candidates = collectChannelStreamCandidates(tuned, detail);
  for (let i = idx; i < candidates.length; i++) {
    const cleaned = sanitizePlaybackUrl(candidates[i]);
    if (cleaned) return cleaned;
  }
  return "";
}

/**
 * True when the channel is operating Continuous Loop (join-NOW applies).
 * Ordinary one_time / daily / weekly / monthly do not seek from elapsed.
 * @param {object|null} nowPayload  /channels/now payload
 * @returns {boolean}
 */
export function isContinuousChannelNow(nowPayload) {
  const mode =
    (nowPayload &&
      nowPayload.channel &&
      String(nowPayload.channel.loop_mode || "").toLowerCase()) ||
    "";
  return mode === "continuous";
}

/**
 * Join-NOW start offset (seconds) for continuous channels.
 * Preserves backend elapsed_seconds; clamps to program runtime when known.
 * Returns 0 for non-continuous, missing current, or just-started programs.
 * Ordinary VOD callers must not use this helper.
 *
 * @param {object|null} nowPayload
 * @returns {number}
 */
export function resolveChannelJoinStartTime(nowPayload) {
  if (!nowPayload || !nowPayload.current) {
    return 0;
  }
  if (!isContinuousChannelNow(nowPayload)) {
    return 0;
  }
  // Live / open-ended YouTube & Vimeo embeds have no join-NOW timeline — seeking
  // causes provider errors ("live stream recording is not available"). Continuous
  // YouTube/Vimeo VOD (or finite scheduled airings) still join at elapsed.
  const asset = nowPayload.media_asset || {};
  const current = nowPayload.current || {};
  const meta = (asset && asset.metadata) || {};
  const caps = Array.isArray(asset.capabilities) ? asset.capabilities : [];
  const source = String(asset.sourceType || "").toLowerCase();
  const primaryUrl =
    (asset.primary && asset.primary.url) ||
    meta.stream_source_url ||
    "";
  const entryKind = String(current.entry_kind || "").toLowerCase();
  const liveState = String(current.live_state || "").toLowerCase();
  const isEmbedProvider =
    source === "youtube" ||
    source === "vimeo" ||
    /youtube\.com|youtu\.be|vimeo\.com/i.test(String(primaryUrl));
  const isLiveLike =
    !!current.is_open_ended ||
    String(asset.presentationKind || "").toLowerCase() === "live" ||
    liveState === "live" ||
    entryKind === "live" ||
    entryKind === "live_event" ||
    caps.indexOf("live") >= 0 ||
    meta.duration_required === false ||
    /youtube\.com\/live\//i.test(String(primaryUrl));
  if (isEmbedProvider && isLiveLike) {
    return 0;
  }
  // Authoritative media / final runtime when present (content + ads chain).
  const contentRuntime = Number(
    current.content_runtime_seconds != null
      ? current.content_runtime_seconds
      : current.final_runtime_seconds != null
        ? current.final_runtime_seconds
        : meta.presentation_duration_seconds
  );
  const hasContentDuration = isFinite(contentRuntime) && contentRuntime > 0;
  // Finite scheduled airing: backend only emits elapsed when starts_at+ends_at exist.
  // Allow join-NOW from schedule-window runtime even when content_runtime was never
  // materialized on the entry (common for YouTube VOD programs).
  const hasFiniteSchedule =
    !current.is_open_ended &&
    !!current.ends_at &&
    Number(current.runtime_seconds) > 0;
  // Embed without VOD duration and without a finite schedule window → do not seek
  // (open-ended / unknown live-like shapes that missed isLiveLike signals).
  if (isEmbedProvider && !hasContentDuration && !hasFiniteSchedule) {
    return 0;
  }
  const elapsed = Math.floor(Number(nowPayload.current.elapsed_seconds));
  if (!isFinite(elapsed) || elapsed <= 0) {
    return 0;
  }
  const runtime = Number(
    hasContentDuration
      ? contentRuntime
      : nowPayload.current.runtime_seconds != null
        ? nowPayload.current.runtime_seconds
        : meta.presentation_duration_seconds
  );
  if (isFinite(runtime) && runtime > 1) {
    // Stay inside the program window (avoid seeking past end).
    return Math.min(elapsed, Math.max(0, runtime - 1));
  }
  return elapsed;
}

/**
 * Next poll delay (ms) from current program ends_at. Caps between 5s and 60s.
 * @param {object|null} nowPayload
 * @param {number} [skewMs]
 * @returns {number}
 */
export function nextChannelNowPollMs(nowPayload, skewMs) {
  const skew = typeof skewMs === "number" ? skewMs : 1500;
  const max = 60000;
  const min = 5000;
  const endsAt =
    nowPayload && nowPayload.current && nowPayload.current.ends_at
      ? Date.parse(nowPayload.current.ends_at)
      : NaN;
  if (!isFinite(endsAt)) {
    return 30000;
  }
  const until = endsAt - Date.now() - skew;
  if (until <= 0) {
    return min;
  }
  return Math.max(min, Math.min(max, until));
}

/**
 * Safe CSS url() for banners / slate (rejects javascript/data).
 * @param {string} url
 * @returns {string|undefined}
 */
export function safeCssBackgroundUrl(url) {
  if (!url || typeof url !== "string") return undefined;
  var cleaned = String(url).trim();
  if (!/^https?:\/\//i.test(cleaned) && !cleaned.startsWith("/")) {
    return undefined;
  }
  if (/[)"']/.test(cleaned)) {
    cleaned = cleaned.replace(/[)"']/g, "");
  }
  return cleaned ? 'url("' + cleaned + '")' : undefined;
}
